import React from "react";
import { Anchor, ArrowRight, UserPlus, LogIn, AtSign, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Login() {
  const handleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 relative flex flex-col">
      <div className="absolute inset-0 scanline pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-8 py-6">
        <Link to="/" className="flex items-center gap-3" data-testid="login-home-link">
          <div className="h-9 w-9 border border-emerald-500/50 flex items-center justify-center">
            <Anchor className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm tracking-[0.3em]">PROJECTIVY</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Sentinel · v18.48
            </div>
          </div>
        </Link>
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="max-w-md w-full border border-zinc-800 bg-[#0b0b0d] p-8 sm:p-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-3 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse" />
            Initiate Docking
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl leading-tight">
            New here?
            <br />
            <span className="text-zinc-500">Sign up first.</span>
          </h1>
          <p className="text-xs text-zinc-400 mt-5 leading-relaxed">
            Every Sentinel account is bound to a Google identity. First-time
            users register with Google, then claim a public{" "}
            <span className="text-amber-300 font-mono">@username</span> so
            others can send AEGIS to their wallet by name.
          </p>

          {/* Sign-up CTA (primary) */}
          <button
            onClick={handleAuth}
            data-testid="signup-google-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 border border-emerald-500 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.35em]"
          >
            <UserPlus className="h-4 w-4" />
            Sign up with Google
            <ArrowRight className="h-4 w-4" />
          </button>

          {/* What happens next — three crisp bullets */}
          <ul className="mt-6 space-y-2 text-[11px] text-zinc-400">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">①</span>
              <span>
                <span className="text-zinc-200">Google handshake</span> —
                authorize once, no password to remember.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">②</span>
              <span>
                <span className="text-zinc-200">Claim your handle</span> — pick{" "}
                <AtSign className="inline h-3 w-3 -mt-0.5 text-amber-400" />
                <span className="font-mono text-amber-300">yourname</span> for
                your wallet (people send AEGIS via this).
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">③</span>
              <span>
                <span className="text-zinc-200">Verify a truth</span> — mint
                AEGIS the moment your first fact passes triangulation.
              </span>
            </li>
          </ul>

          {/* Divider + Sign-in for returning users */}
          <div className="relative my-6 flex items-center" aria-hidden="true">
            <div className="flex-1 h-px bg-zinc-900" />
            <span className="px-3 text-[9px] uppercase tracking-[0.3em] text-zinc-600">
              Already have an account?
            </span>
            <div className="flex-1 h-px bg-zinc-900" />
          </div>

          <button
            onClick={handleAuth}
            data-testid="signin-google-btn"
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 border border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors text-[11px] uppercase tracking-[0.3em]"
          >
            <LogIn className="h-3.5 w-3.5" />
            Sign in with Google
          </button>

          {/* Email/password escape hatch — works without Google OAuth */}
          <div className="mt-6 pt-5 border-t border-zinc-900 text-center">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 mb-3">
              Google not working?
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to="/signup"
                data-testid="signup-email-link"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 transition-colors text-[10px] uppercase tracking-[0.3em]"
              >
                <UserPlus className="h-3 w-3" />
                Sign up with email
              </Link>
              <Link
                to="/signin"
                data-testid="signin-email-link"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors text-[10px] uppercase tracking-[0.3em]"
              >
                <LogIn className="h-3 w-3" />
                Sign in with email
              </Link>
            </div>
            <div className="text-[9px] text-zinc-600 mt-3 leading-relaxed">
              Same wallet, same handle, same AEGIS — just no Google handshake.
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-zinc-900 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            <ShieldCheck className="h-3 w-3 text-emerald-500/70" />
            <span>528 Hz · Safety Tether armed</span>
          </div>
        </div>
      </div>

      <footer className="relative z-10 px-8 py-6 text-[10px] uppercase tracking-[0.35em] text-zinc-600 text-center">
        By continuing, you accept the Humanitarian Pulse handshake.
      </footer>
    </div>
  );
}
